/**
 * 持卡人偏好（服务端持久化）· PAGE-CHD-PRF-003 · US-PREF-2
 * - GET/PUT /profile/preferences（契约冻结于 sprint-contract §3.1，字段名不得单方变更）
 * - localStorage 降级缓存（GET 成功写缓存含 cachedAt；GET 失败读缓存；PUT 成功更新缓存）
 * - USE_MOCK：sessionStorage mock 状态机（version 递增 + 409 模拟开关，E8）
 * - budgetCap 前端即时校验（对齐后端 R4 正则 + 闭区间，金额全程 string 不转 number 承载）
 * - 语言优先级链唯一实现点 applyServerLocale（URL > 服务端 > localStorage > 默认，A6）
 */
import i18n from '@/i18n';
import { CHD_LANG_STORAGE_KEY, type ChdLang } from '@/i18n/resources';
import { getPreferredLang, parseLangFromPath } from '@/i18n/localePath';
import { cardholderGet, cardholderPut, loadSession, USE_MOCK, type ApiResult } from './client';

// ================= 契约类型（合同 §3.1 冻结） =================

/** GET 响应（后端 PreferencesView） */
export type PreferencesView = {
  locale: string;
  notifyEmail: boolean;
  notifyPush: boolean;
  /** 金额以 string 传输（A3：前端不得强转 number）；null = 不限制 */
  budgetCap: string | null;
  prefVersion: number;
  localeVersion: number;
};

/** PUT 请求（后端 UpdatePreferencesRequest） */
type UpdatePreferencesRequest = {
  /** 缺省/null = 不改 locale（非 VAL_400，E11） */
  locale?: string;
  notifyEmail: boolean;
  notifyPush: boolean;
  /** ""/null = 不限制 */
  budgetCap: string | null;
  /** 0 = 仅无行时宽容首写；行存在时落后即 CONC_409（E2） */
  prefVersion: number;
  /** locale 变更时必填 */
  localeVersion: number;
};

/** 偏好操作失败分类（信封 code vs 传输失败，页面按 kind 走对应 UX 流） */
type PreferencesError =
  | { kind: 'network'; message: string; timedOut: boolean }
  | { kind: 'conflict'; message: string }
  | { kind: 'validation'; message: string }
  | { kind: 'other'; message: string };

type PreferencesResult =
  | { ok: true; data: PreferencesView }
  | { ok: false; error: PreferencesError };

function classifyApiError(res: { ok: false; error: string; code?: string }): PreferencesError {
  if (res.code === 'CONC_409') {
    return { kind: 'conflict', message: res.error };
  }
  if (res.code === 'VAL_400') {
    return { kind: 'validation', message: res.error };
  }
  if (!res.code) {
    // 无信封 code：网络错误 / 超时（status 0 / invalid_json / timeout 竞速）
    return { kind: 'network', message: res.error, timedOut: res.error === 'timeout' };
  }
  return { kind: 'other', message: res.error };
}

// ================= 超时（E3：GET/PUT 超时分别走失败路径） =================

const PREF_REQUEST_TIMEOUT_MS = 15000;

/** 竞速超时包装：不中断底层连接（无法透传 AbortSignal），仅让调用方按时落到失败分支 */
function withTimeout(p: Promise<ApiResult<PreferencesView>>): Promise<ApiResult<PreferencesView>> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: 'timeout' }), PREF_REQUEST_TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve({ ok: false, error: 'network_error' });
      },
    );
  });
}

// ================= localStorage 降级缓存（A4：服务端语义缓存封装在本模块） =================

const PREF_CACHE_KEY = 'allinks_chd_pref_cache';
/** 旧版纯本机持久化 key（E9 兼容读取 + 单向迁移，不删除旧 key 以支持回退） */
const LEGACY_PREF_KEY = 'allinks_chd_preferences';

type PreferencesCache = { cachedAt: number; data: PreferencesView };

export function readPreferencesCache(): PreferencesCache | null {
  try {
    const raw = localStorage.getItem(PREF_CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw) as PreferencesCache;
      if (c && typeof c.cachedAt === 'number' && c.data) {
        return { cachedAt: c.cachedAt, data: normalizePreferences(c.data) };
      }
    }
  } catch {
    /* ignore */
  }
  // E9：旧结构 {notifyEmail,notifyPush,budgetCap}（无 cachedAt/version）→ 迁移为新缓存结构
  try {
    const legacyRaw = localStorage.getItem(LEGACY_PREF_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw) as {
        notifyEmail?: boolean;
        notifyPush?: boolean;
        budgetCap?: string;
      };
      const migrated: PreferencesCache = {
        cachedAt: Date.now(),
        data: {
          locale: toServerLocale(getPreferredLang()),
          notifyEmail: legacy.notifyEmail !== false,
          notifyPush: legacy.notifyPush !== false,
          budgetCap: legacy.budgetCap && legacy.budgetCap.trim() !== '' ? legacy.budgetCap : null,
          prefVersion: 0,
          localeVersion: 0,
        },
      };
      writePreferencesCache(migrated.data);
      return migrated;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writePreferencesCache(data: PreferencesView): void {
  try {
    localStorage.setItem(PREF_CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {
    /* ignore */
  }
}

// ================= 默认值 / 规范化 =================

/** 未建档默认（对齐后端 GET 默认值语义 R3：notify 全开、budgetCap 不限制、version 0） */
function defaultPreferences(): PreferencesView {
  return {
    locale: toServerLocale(getPreferredLang()),
    notifyEmail: true,
    notifyPush: true,
    budgetCap: null,
    prefVersion: 0,
    localeVersion: 0,
  };
}

/** 防御性规范化：容错后端轻微违约（budgetCap ""→null、version 缺省 0），金额不转 number 承载 */
function normalizePreferences(raw: PreferencesView): PreferencesView {
  return {
    locale: typeof raw?.locale === 'string' ? raw.locale : toServerLocale(getPreferredLang()),
    notifyEmail: raw?.notifyEmail !== false,
    notifyPush: raw?.notifyPush !== false,
    budgetCap:
      raw?.budgetCap == null || raw.budgetCap === '' ? null : String(raw.budgetCap).trim(),
    prefVersion: Number(raw?.prefVersion) || 0,
    localeVersion: Number(raw?.localeVersion) || 0,
  };
}

// ================= budgetCap 即时校验（对齐后端 R4 值域） =================

/** R4 正则白名单：无前导零 / 无千分位 / 最多两位小数 / 非科学计数法 */
const BUDGET_CAP_RE = /^(0|[1-9]\d*)(\.\d{1,2})?$/;
const BUDGET_CAP_MAX = 999999999.99;

/** 表单即时校验：空 = 不限制；否则须落闭区间 (0, 999999999.99]（0/0.00 无合法语义） */
export function isValidBudgetCapInput(raw: string): boolean {
  const v = raw.trim();
  if (v === '') {
    return true;
  }
  if (!BUDGET_CAP_RE.test(v)) {
    return false;
  }
  const n = Number(v);
  return n > 0 && n <= BUDGET_CAP_MAX;
}

// ================= locale 映射（N3） =================

/** UI 语言 → 服务端 locale 枚举（白名单 zh-CN/en/zh-TW 取子集） */
function toServerLocale(lang: ChdLang): string {
  return lang === 'en' ? 'en' : 'zh-CN';
}

/**
 * 服务端 locale → UI 语言：zh-CN/zh-TW → zh（暂无繁体资源，UI 不得声称繁体，E10）；
 * en → en；非法/未知 → null（调用方回落既有链）。
 */
function chdLangFromServerLocale(locale: string | null | undefined): ChdLang | null {
  if (locale === 'en') {
    return 'en';
  }
  if (locale === 'zh-CN' || locale === 'zh-TW' || locale === 'zh') {
    return 'zh';
  }
  return null;
}

/**
 * 语言优先级链唯一实现点（A6）：URL /:lang > 服务端 > localStorage > 默认（zh，N3）。
 * URL 已有前缀时不被服务端值覆盖；结果写回 localStorage 并同步 i18n。
 */
export function applyServerLocale(serverLocale: string | null | undefined): ChdLang {
  const urlLang = parseLangFromPath(window.location.pathname);
  const serverLang = chdLangFromServerLocale(serverLocale);
  const finalLang = urlLang ?? serverLang ?? getPreferredLang();
  try {
    localStorage.setItem(CHD_LANG_STORAGE_KEY, finalLang);
  } catch {
    /* ignore */
  }
  if (i18n.language !== finalLang) {
    void i18n.changeLanguage(finalLang);
  }
  return finalLang;
}

// ================= mock 状态机（E8：sessionStorage + version 递增 + 409 模拟开关） =================

const MOCK_PREF_KEY = 'allinks_chd_mock_preferences';
/** 409 演示开关：控制台 sessionStorage.setItem('allinks_chd_mock_pref_409','1') → 下次 PUT 返回 CONC_409（触发后自动复位） */
const MOCK_PREF_409_KEY = 'allinks_chd_mock_pref_409';

function readMockState(): PreferencesView {
  try {
    const raw = sessionStorage.getItem(MOCK_PREF_KEY);
    if (raw) {
      const s = JSON.parse(raw) as PreferencesView;
      if (s && typeof s.prefVersion === 'number') {
        return normalizePreferences(s);
      }
    }
  } catch {
    /* ignore */
  }
  return defaultPreferences();
}

function writeMockState(p: PreferencesView): void {
  try {
    sessionStorage.setItem(MOCK_PREF_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

/** USE_MOCK=true 时页面无真实网络请求（E8）；mock 与真实路径行为对齐（409/VAL_400/version 递增） */
function mockUpdate(req: UpdatePreferencesRequest): PreferencesResult {
  if (sessionStorage.getItem(MOCK_PREF_409_KEY) === '1') {
    sessionStorage.removeItem(MOCK_PREF_409_KEY);
    return { ok: false, error: { kind: 'conflict', message: 'CONC_409: mock 模拟乐观锁冲突' } };
  }
  if (req.locale != null && req.locale !== 'zh-CN' && req.locale !== 'en' && req.locale !== 'zh-TW') {
    return { ok: false, error: { kind: 'validation', message: 'VAL_400: mock locale 白名单校验失败' } };
  }
  if (req.budgetCap != null && req.budgetCap !== '' && !isValidBudgetCapInput(req.budgetCap)) {
    return { ok: false, error: { kind: 'validation', message: 'VAL_400: mock budgetCap 校验失败' } };
  }
  const cur = readMockState();
  const firstWrite = cur.prefVersion === 0;
  if (req.prefVersion !== cur.prefVersion && !(firstWrite && req.prefVersion === 0)) {
    return { ok: false, error: { kind: 'conflict', message: 'CONC_409: mock prefVersion 冲突' } };
  }
  const localeChanged = req.locale != null && req.locale !== cur.locale;
  if (
    localeChanged &&
    req.localeVersion !== cur.localeVersion &&
    !(cur.localeVersion === 0 && req.localeVersion === 0)
  ) {
    return { ok: false, error: { kind: 'conflict', message: 'CONC_409: mock localeVersion 冲突' } };
  }
  const next: PreferencesView = {
    locale: localeChanged ? (req.locale as string) : cur.locale,
    notifyEmail: req.notifyEmail,
    notifyPush: req.notifyPush,
    budgetCap: req.budgetCap === '' || req.budgetCap == null ? null : req.budgetCap,
    prefVersion: cur.prefVersion + 1,
    localeVersion: localeChanged ? cur.localeVersion + 1 : cur.localeVersion,
  };
  writeMockState(next);
  return { ok: true, data: { ...next } };
}

// ================= 最近已知快照（LanguageSwitcher 全字段提交的数据源） =================

let lastKnown: PreferencesView | null = readPreferencesCache()?.data ?? null;

/** 最近已知服务端偏好（无已知值时给默认值）；切语言即时 PUT 的全字段数据源（N5） */
function getKnownPreferences(): PreferencesView {
  return lastKnown ? { ...lastKnown } : defaultPreferences();
}

// ================= GET / PUT =================

/** cardholderGet/Put 的 mock thunk 在本模块不可达（USE_MOCK 已先行短路走 mock 状态机），仅为满足签名 */
function unreachableMock(): PreferencesView {
  return readMockState();
}

/** GET /profile/preferences；成功写降级缓存并更新最近已知快照 */
export async function fetchPreferences(): Promise<PreferencesResult> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: readMockState() };
  }
  const res = await withTimeout(
    cardholderGet<PreferencesView>('/profile/preferences', unreachableMock),
  );
  if (!res.ok) {
    return { ok: false, error: classifyApiError(res) };
  }
  const data = normalizePreferences(res.data);
  lastKnown = data;
  writePreferencesCache(data);
  return { ok: true, data: { ...data } };
}

/** PUT 串行队列：保存按钮与切语言即时 PUT 双入口互斥（H4），版本冲突仍由 CONC_409 兜底 */
let putQueue: Promise<unknown> = Promise.resolve();

/** PUT /profile/preferences；成功写缓存并更新快照。不携带 Idempotency-Key（cardholderPut 契约） */
export function updatePreferences(req: UpdatePreferencesRequest): Promise<PreferencesResult> {
  if (USE_MOCK) {
    // mock 状态机同步执行，无需排队
    return Promise.resolve(mockUpdate(req));
  }
  const run = putQueue.then(
    () => doPut(req),
    () => doPut(req),
  );
  putQueue = run.catch(() => undefined);
  return run;
}

async function doPut(req: UpdatePreferencesRequest): Promise<PreferencesResult> {
  const body: UpdatePreferencesRequest = {
    ...req,
    budgetCap: req.budgetCap === '' || req.budgetCap == null ? null : req.budgetCap.trim(),
  };
  const res = await withTimeout(
    cardholderPut<PreferencesView>('/profile/preferences', body, unreachableMock),
  );
  if (!res.ok) {
    return { ok: false, error: classifyApiError(res) };
  }
  const data = normalizePreferences(res.data);
  lastKnown = data;
  writePreferencesCache(data);
  return { ok: true, data: { ...data } };
}

// ================= 切语言服务端同步（N5 方案 A：登录会话才 PUT） =================

type LocaleSyncOutcome = 'synced' | 'anonymous' | 'failed';

/**
 * 切换语言后同步服务端 locale（调用方负责 300ms 防抖，N4）。
 * 全字段提交最近已知值（表单 pending 值不在此路径静默提交）；
 * 未登录（无 accessToken）不 PUT，纯本地；失败不影响本地切换。
 */
export async function syncLocalePreference(lang: ChdLang): Promise<LocaleSyncOutcome> {
  const session = loadSession();
  if (!session?.accessToken) {
    return 'anonymous';
  }
  const known = getKnownPreferences();
  const res = await updatePreferences({
    locale: toServerLocale(lang),
    notifyEmail: known.notifyEmail,
    notifyPush: known.notifyPush,
    budgetCap: known.budgetCap,
    prefVersion: known.prefVersion,
    localeVersion: known.localeVersion,
  });
  return res.ok ? 'synced' : 'failed';
}

// ================= 未保存更改 pending（E5：刷新后提示「有未保存的更改」） =================

const PREF_PENDING_KEY = 'allinks_chd_pref_pending';

export type PendingPrefsForm = { notifyEmail: boolean; notifyPush: boolean; budgetCap: string };

export function readPendingPrefs(): PendingPrefsForm | null {
  try {
    const raw = sessionStorage.getItem(PREF_PENDING_KEY);
    if (!raw) {
      return null;
    }
    const p = JSON.parse(raw) as PendingPrefsForm;
    if (
      typeof p?.notifyEmail === 'boolean' &&
      typeof p?.notifyPush === 'boolean' &&
      typeof p?.budgetCap === 'string'
    ) {
      return p;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function writePendingPrefs(p: PendingPrefsForm | null): void {
  try {
    if (p == null) {
      sessionStorage.removeItem(PREF_PENDING_KEY);
    } else {
      sessionStorage.setItem(PREF_PENDING_KEY, JSON.stringify(p));
    }
  } catch {
    /* ignore */
  }
}
